/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 23-09-2026
 *
 * Het scherm achter de wachtwoordlink uit de mail (`#/ww/<token>`), de ng21-opvolger van
 * `app/states/wwlink/` (golf 4e). Een eigen scherm en geen dialoog, zodat het ook op een telefoon
 * behoorlijk staat — dat was de reden waarom het in 2021 een eigen state werd.
 *
 * Er wordt bewust niet automatisch ingelogd na het wijzigen: de gebruiker kan in meerdere domeinen
 * bestaan, en welk domein hij bedoelt weet dit scherm niet.
 */
import {ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {TranslatePipe} from '@ngx-translate/core';
import {Sihwlog} from 'sihw-ngx';
import {SessieService} from '../../../services/sessie.service';
import {VERSIE} from '../../../app/versie';

@Component({
    selector: 'dl-wachtwoordlink',
    templateUrl: './dl-wachtwoordlink.component.html',
    styleUrls: ['./dl-wachtwoordlink.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, RouterLink, TranslatePipe]
})
export class DlWachtwoordlinkComponent implements OnInit {

    private readonly log = new Sihwlog('info', 'DlWachtwoordlinkComponent');
    private readonly sessie = inject(SessieService);
    private readonly route = inject(ActivatedRoute);
    private readonly opruimen = inject(DestroyRef);

    /** Het versienummer onderaan het scherm. */
    protected readonly versie = VERSIE;

    /** De vertaalsleutel van de foutmelding, of leeg. */
    protected readonly fout = signal('');

    /** De vertaalsleutel van de gelukt-melding, of leeg. */
    protected readonly succes = signal('');

    /** De twee ingevulde wachtwoorden. */
    protected readonly formulier = {ww: '', ww2: ''};

    /** Het token uit de url. */
    private token = '';

    /**
     * Volgt het token in de url. Het abonnement en niet de snapshot: bij een tweede link in dezelfde tab
     * hergebruikt de router dit component en draait `ngOnInit` niet opnieuw — dan zou de uitkomst van de
     * vorige link blijven staan.
     */
    ngOnInit(): void {
        this.route.paramMap.pipe(takeUntilDestroyed(this.opruimen))
            .subscribe(params => void this.controleerLink(params.get('wwlink') ?? ''));
    }

    /**
     * Kijkt of deze link nog geldig is.
     */
    private async controleerLink(token: string): Promise<void> {
        this.token = token;
        this.fout.set('');
        this.succes.set('');
        this.formulier.ww = '';
        this.formulier.ww2 = '';
        if (!token) {
            this.fout.set('WACHTWOORDVERGETEN.WIJZIG.ONGELDIG');
            return;
        }
        try {
            if (!await this.sessie.isWachtwoordlink(token)) {
                this.fout.set('WACHTWOORDVERGETEN.WIJZIG.ONGELDIG');
            }
        } catch (fout) {
            this.log.error(fout);
            this.fout.set('ERROR.MISC');
        }
    }

    /**
     * Of de twee wachtwoorden gelijk zijn en lang genoeg.
     */
    protected geldig(): boolean {
        return this.formulier.ww.length >= 6 && this.formulier.ww === this.formulier.ww2;
    }

    /**
     * Zet het nieuwe wachtwoord.
     */
    protected async verstuurWachtwoord(): Promise<void> {
        this.fout.set('');
        this.succes.set('');
        try {
            if (await this.sessie.updateWachtwoord(this.token, this.formulier.ww)) {
                this.succes.set('WACHTWOORDVERGETEN.WIJZIG.GEWIJZIGD');
            } else {
                this.fout.set('WACHTWOORDVERGETEN.WIJZIG.ONGELDIG');
            }
        } catch (fout) {
            this.log.error(fout);
            this.fout.set('ERROR.MISC');
        }
    }
}
